'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Solicitud } from '@/types/solicitud';
import { solicitudService } from '@/lib/api/solicitudService';
import { userService } from '@/lib/api/userService';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import ErrorMessage from '@/components/common/ErrorMessage';
import { Input } from '@/components/ui/Input';
import { SolicitudDetail } from '../solicitudes/SolicitudDetail';

type Analista = { userName: string; userEstatus?: string };

const BUEN_BURO_ESTATUS_OPTIONS = [
  { value: '', label: '(sin)' },
  { value: 'No aplica', label: 'No aplica' },
  { value: 'Aceptado', label: 'Aceptado' },
  { value: 'En espera', label: 'En espera' },
  { value: 'Rechazado', label: 'Rechazado' },
];

const ESTATUS_ANALISIS_OPTIONS = [
  { value: '', label: '(sin)' },
  { value: 'Pendiente', label: 'Pendiente' },
  { value: 'En Analisis', label: 'En Análisis' },
  { value: 'En Espera', label: 'En Espera' },
  { value: 'Terminado', label: 'Terminado' },
];

function calcularDiasTranscurridos(fechaEntregaARiesgos: string | undefined): number {
  if (!fechaEntregaARiesgos) return 0;
  const fecha = new Date(fechaEntregaARiesgos);
  const hoy = new Date();
  const diffTime = hoy.getTime() - fecha.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

function esUrlDrive(val: string | undefined): boolean {
  if (!val || typeof val !== 'string') return false;
  const t = val.trim();
  return t.startsWith('http') && t.includes('drive');
}

function formatFechaAPartir(fecha: string | undefined): string {
  if (!fecha) return '';
  return new Date(fecha).toLocaleDateString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function formatCurrency(value: number | undefined): string {
  if (value === undefined || value === null) return '$0.00';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(value);
}

const MesaDeAnalisisManagement: React.FC = () => {
  const { user: authUser, isFieldAllowed } = useAuth();
  const canEditAnalistaNombre = isFieldAllowed('analistaNombre');
  const canEditEstatusAnalisis = isFieldAllowed('estatusAnalisis');
  const canEditBuenBuroMontoOferta = isFieldAllowed('buenBuroMontoOferta');
  const canEditBuenBuroEstatus = isFieldAllowed('buenBuroEstatus');
  const canEditBuenBuroMontoAceptado = isFieldAllowed('buenBuroMontoAceptado');
  const pathname = usePathname();
  const authSub = authUser?.sub ?? null;
  const [solicitudes, setSolicitudes] = useState<Solicitud[]>([]);
  const [analistas, setAnalistas] = useState<Analista[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState<Solicitud | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Filtros (como en la imagen: Desde, Hasta, Producto, Region, Estatus, Analizado Por, Asesor, Empresa, Cliente)
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [producto, setProducto] = useState<string>('General');
  const [region, setRegion] = useState<string>('General');
  const [estatusFiltro, setEstatusFiltro] = useState<string>('General');
  const [analizadoPor, setAnalizadoPor] = useState<string>('General');
  const [asesor, setAsesor] = useState('');
  const [empresa, setEmpresa] = useState('');
  const [cliente, setCliente] = useState('');

  const load = async () => {
    if (!authSub) {
      setIsLoading(false);
      return;
    }
    setError(null);
    setIsRefreshing(true);
    try {
      const [sres, ares] = await Promise.all([
        solicitudService.getEntregadosARiesgos(authSub),
        userService.getUsersByPuesto('ANALISTA DE RIESGOS'),
      ]);
      if (sres.data) setSolicitudes(sres.data);
      else setError(sres.error || 'Error al cargar solicitudes');
      if (ares.data) {
        const activos = (ares.data as any[])
          .filter((u) => (u.userEstatus || '').toLowerCase() === 'activo')
          .map((u) => ({ userName: u.userName || u.name, userEstatus: u.userEstatus }));
        setAnalistas(activos);
      }
    } catch (e) {
      setError('Error de conexión al cargar Mesa de Análisis');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, [authSub, pathname]);


  // Backend ya devuelve solo ENTREGA A RIESGOS (getEntregadosARiesgos)
  const mesaBase = useMemo(() => solicitudes || [], [solicitudes]);

  const filtered = useMemo(() => {
    let list = mesaBase;

    if (fechaInicio) {
      list = list.filter((s) => {
        const fecha = (s.fechaEntregaARiesgos || '').substring(0, 10);
        return fecha >= fechaInicio;
      });
    }
    if (fechaFin) {
      list = list.filter((s) => {
        const fecha = (s.fechaEntregaARiesgos || '').substring(0, 10);
        return fecha <= fechaFin;
      });
    }
    if (producto !== 'General') {
      list = list.filter((s) => (s.producto || '').toLowerCase().includes(producto.toLowerCase()));
    }
    if (region !== 'General') {
      list = list.filter((s) => (s.regionNombre || '') === region);
    }
    if (estatusFiltro !== 'General') {
      list = list.filter((s) => (s.estatusAnalisis || '') === estatusFiltro);
    }
    if (analizadoPor !== 'General') {
      if (analizadoPor === '') {
        list = list.filter((s) => !(s.analistaNombre || '').trim());
      } else {
        list = list.filter((s) => (s.analistaNombre || '') === analizadoPor);
      }
    }
    if (asesor.trim()) {
      const t = asesor.toLowerCase();
      list = list.filter((s) => (s.asesorNombre || '').toLowerCase().includes(t));
    }
    if (empresa.trim()) {
      const t = empresa.toLowerCase();
      list = list.filter((s) => (s.clienteEmpresa || '').toLowerCase().includes(t));
    }
    if (cliente.trim()) {
      const t = cliente.toLowerCase();
      list = list.filter((s) =>
        [s.clienteNombre, s.clienteApellidoPaterno, s.clienteApellidoMaterno]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(t)
      );
    }

    return [...list].sort((a, b) =>
      (a.fechaEntregaARiesgos || '').localeCompare(b.fechaEntregaARiesgos || '')
    );
  }, [mesaBase, fechaInicio, fechaFin, producto, region, estatusFiltro, analizadoPor, asesor, empresa, cliente]);

  // Cambios pendientes por fila (no se guardan hasta presionar botón)
  const [pendingChanges, setPendingChanges] = useState<Record<string, Record<string, string | number>>>({});
  const [savingRows, setSavingRows] = useState<Record<string, boolean>>({});

  const updateFieldLocal = useCallback((id: string, field: string, value: string | number) => {
    setSolicitudes((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s))
    );
    setPendingChanges((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));
  }, []);

  const saveRow = useCallback(async (id: string) => {
    const changes = pendingChanges[id];
    if (!changes) return;
    setSavingRows((prev) => ({ ...prev, [id]: true }));
    setError(null);
    try {
      let lastData: Solicitud | null = null;
      for (const [field, value] of Object.entries(changes)) {
        const res = await solicitudService.updateField(id, field, String(value), 'Mesa de Analisis');
        if (!res.data) {
          setError(res.error || `Error al guardar ${field}`);
          return;
        }
        lastData = res.data;
      }
      if (lastData) {
        setSolicitudes((prev) => prev.map((s) => (s.id === id ? lastData! : s)));
        if (selectedSolicitud?.id === id) setSelectedSolicitud(lastData);
      }
      setPendingChanges((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch {
      setError('Error de conexión al guardar');
    } finally {
      setSavingRows((prev) => ({ ...prev, [id]: false }));
    }
  }, [pendingChanges, selectedSolicitud]);

  const handleVerDetalle = (s: Solicitud) => {
    setSelectedSolicitud(s);
    setIsDetailOpen(true);
  };

  const handleUpdateFromDetail = (updated: Solicitud) => {
    setSelectedSolicitud(updated);
    setSolicitudes((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  };

  const productosOptions = useMemo(() => {
    const set = new Set<string>();
    mesaBase.forEach((s) => s.producto && set.add(s.producto));
    return ['General', ...Array.from(set).sort()];
  }, [mesaBase]);

  const regionesOptions = useMemo(() => {
    const set = new Set<string>();
    mesaBase.forEach((s) => s.regionNombre && set.add(s.regionNombre));
    return ['General', ...Array.from(set).sort()];
  }, [mesaBase]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mesa de Análisis</h1>
          <p className="text-sm text-gray-500 mt-1">
            Solicitudes en estatus Entregado a Riesgos. Asignación de analista, estatus y Buen Buró.
          </p>
        </div>
        <Button variant="secondary" onClick={load} disabled={isRefreshing}>
          {isRefreshing ? 'Actualizando…' : 'Actualizar'}
        </Button>
      </div>

      {error && <ErrorMessage message={error} />}

      {/* Filtros: Desde, Hasta, Producto, Region, Estatus, Analizado Por, Asesor, Empresa, Cliente */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              min="2019-01-01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              min="2019-01-01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Producto</label>
            <select
              className="w-full px-3 py-2 border rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500"
              value={producto}
              onChange={(e) => setProducto(e.target.value)}
            >
              {productosOptions.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Región</label>
            <select
              className="w-full px-3 py-2 border rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
            >
              {regionesOptions.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estatus</label>
            <select
              className="w-full px-3 py-2 border rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500"
              value={estatusFiltro}
              onChange={(e) => setEstatusFiltro(e.target.value)}
            >
              <option value="General">General</option>
              {ESTATUS_ANALISIS_OPTIONS.filter((o) => o.value).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Analizado Por</label>
            <select
              className="w-full px-3 py-2 border rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500"
              value={analizadoPor}
              onChange={(e) => setAnalizadoPor(e.target.value)}
            >
              <option value="General">General</option>
              <option value="">Sin asignar</option>
              {analistas.map((a) => (
                <option key={a.userName} value={a.userName}>{a.userName}</option>
              ))}
            </select>
          </div>
          <Input label="Asesor" value={asesor} onChange={(e) => setAsesor(e.target.value)} placeholder="Asesor" />
          <Input label="Empresa" value={empresa} onChange={(e) => setEmpresa(e.target.value)} placeholder="Empresa" />
          <Input label="Cliente" value={cliente} onChange={(e) => setCliente(e.target.value)} placeholder="Cliente" />
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-gray-500">
            Mostrando <span className="font-medium text-gray-700">{filtered.length}</span> registros
          </span>
          <div className="flex gap-2">
            <Button variant="primary" onClick={load} disabled={isRefreshing}>
              {isRefreshing ? 'Consultando...' : 'Consultar'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setFechaInicio('');
                setFechaFin('');
                setProducto('General');
                setRegion('General');
                setEstatusFiltro('General');
                setAnalizadoPor('General');
                setAsesor('');
                setEmpresa('');
                setCliente('');
              }}
            >
              Limpiar
            </Button>
          </div>
        </div>
      </div>

      {/* Tabla tipo imagen: una fila por solicitud con toda la info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="min-w-[1000px] w-full text-sm table-fixed">
          <thead className="bg-gray-50 text-gray-700 sticky top-0">
            <tr>
              <th className="text-left p-2 border-r border-gray-200 w-10">#</th>
              <th className="text-left p-2 border-r border-gray-200 w-32">Asesor</th>
              <th className="text-left p-2 border-r border-gray-200 w-24">Empresa</th>
              <th className="text-left p-2 border-r border-gray-200 min-w-0" style={{ width: '18%' }}>Cliente</th>
              <th className="text-left p-2 border-r border-gray-200 w-28">Entregado a Riesgos</th>
              <th className="text-left p-2 border-r border-gray-200 w-24">A partir del</th>
              <th className="text-left p-2 border-r border-gray-200 w-20">Días Trans.</th>
              <th className="text-left p-2 border-r border-gray-200 w-36">Analista</th>
              <th className="text-left p-2 border-r border-gray-200" style={{ width: '18%' }}>Estatus</th>
              <th className="text-left p-2 border-r border-gray-200" style={{ width: '22%' }}>Buen Buró</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.map((s, idx) => {
              const diasRaw = s.diasTranscurridos;
              const dias =
                diasRaw !== undefined && diasRaw !== null && String(diasRaw).trim() !== ''
                  ? parseInt(String(diasRaw), 10)
                  : calcularDiasTranscurridos(s.fechaEntregaARiesgos);
              const nombreCliente = [s.clienteNombre, s.clienteApellidoPaterno, s.clienteApellidoMaterno]
                .filter(Boolean)
                .join(' ')
                .trim();
              const driveUrl = s.documentos && esUrlDrive(s.documentos) ? s.documentos.trim() : null;
              const propuesta = s.propuestaBuenBuro;
              const buenBuroOferta = s.buenBuroMontoOferta ?? propuesta?.buenBuroMontoOferta ?? s.montoBuenBuro;
              const buenBuroAceptado = s.buenBuroMontoAceptado ?? propuesta?.buenBuroMontoAceptado;
              const buenBuroEstatus = s.buenBuroEstatus ?? propuesta?.buenBuroEstatus ?? '';
              const valorComentarios =
                pendingChanges[s.id ?? '']?.comentarios !== undefined
                  ? String(pendingChanges[s.id ?? ''].comentarios)
                  : (s.comentarios ? String(s.comentarios).trim() : '');
              return (
                <tr key={s.id ?? idx} className="hover:bg-blue-50/30 transition-colors">
                  <td className="p-2 border-r border-gray-100 text-gray-500 font-medium">{idx + 1}</td>
                  <td className="p-2 border-r border-gray-100">{s.asesorNombre || '—'}</td>
                  <td className="p-2 border-r border-gray-100">{(s.clienteEmpresa || '—').toUpperCase()}</td>
                  <td className="p-2 border-r border-gray-100 align-middle min-w-0 text-center">
                    <div className="flex flex-col gap-0 min-w-0 items-center justify-center w-full">
                      <div className="pb-2">
                        {driveUrl ? (
                          <a
                            href={driveUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 font-medium underline hover:text-blue-800 break-words"
                          >
                            {(nombreCliente || '—').toUpperCase()}
                          </a>
                        ) : (
                          <span className="font-medium text-gray-900 break-words">{(nombreCliente || '—').toUpperCase()}</span>
                        )}
                      </div>
                      <div className="border-t border-gray-300 pt-2 w-full">
                        {s.producto && (
                          <div className="text-xs text-gray-700 pb-1.5">{(s.producto || '').toUpperCase()}</div>
                        )}
                        <div className={`text-xs text-gray-600 ${s.producto ? 'border-t border-gray-300 pt-1.5' : ''}`}>
                          <span className="font-semibold text-gray-700 block mb-0.5">Comentarios:</span>
                          <textarea
                            rows={2}
                            className="w-full mt-0.5 px-2 py-1 text-xs border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y min-h-[3rem] max-h-24"
                            value={valorComentarios}
                            onChange={(e) => s.id && updateFieldLocal(s.id, 'comentarios', e.target.value)}
                            placeholder="Escribe comentarios..."
                          />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-2 border-r border-gray-100 text-gray-700 text-xs">
                    {s.fechaEntregaARiesgos
                      ? new Date(s.fechaEntregaARiesgos).toLocaleString('es-MX', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })
                      : (s.solicitudEstatus || '—')}
                  </td>
                  <td className="p-2 border-r border-gray-100 text-gray-600 text-xs">
                    {formatFechaAPartir(s.fechaEntregaARiesgos) || '—'}
                  </td>
                  <td className="p-2 border-r border-gray-100">
                    <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-800">
                      {dias}
                    </span>
                  </td>
                  <td className="p-2 border-r border-gray-100">
                    {canEditAnalistaNombre ? (
                    <select
                      className="w-full max-w-[140px] px-2 py-1.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 text-sm"
                      value={(s.analistaNombre ?? '').trim()}
                      onChange={(e) => s.id && updateFieldLocal(s.id, 'analistaNombre', e.target.value)}
                    >
                      <option value="">Sin asignar</option>
                      {analistas.map((a) => (
                        <option key={a.userName} value={a.userName}>
                          {a.userName}
                        </option>
                      ))}
                    </select>
                    ) : (
                    <span className="text-sm text-gray-600">{(s.analistaNombre ?? '').trim() || '—'}</span>
                    )}
                  </td>
                  <td className="p-3 border-r border-gray-100 align-top text-center">
                    <div className="flex flex-col gap-0 min-w-0 items-center justify-center">
                      <div className="pb-2 w-full">
                        {canEditEstatusAnalisis ? (
                        <select
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 text-sm"
                          value={(s.estatusAnalisis ?? '').trim()}
                          onChange={(e) => s.id && updateFieldLocal(s.id, 'estatusAnalisis', e.target.value)}
                        >
                          {ESTATUS_ANALISIS_OPTIONS.map((o) => (
                            <option key={o.value || '(sin)'} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                        ) : (
                        <span className="text-sm text-gray-600">{(s.estatusAnalisis ?? '').trim() || '—'}</span>
                        )}
                      </div>
                      <div className="border-t border-gray-300 pt-2 w-full space-y-1.5">
                        <div className="rounded-md bg-amber-50 border border-amber-200 px-2 py-1.5 text-xs">
                          <span className="font-semibold text-amber-800 block mb-0.5">Solicitado</span>
                          <span className="text-amber-900 font-medium">{formatCurrency(s.montoSolicitado)}</span>
                        </div>
                        <div className="rounded-md bg-emerald-50 border border-emerald-200 px-2 py-1.5 text-xs">
                          <span className="font-semibold text-emerald-800 block mb-0.5">Autorizado</span>
                          <span className="text-emerald-900 font-medium">{formatCurrency(s.montoAutorizado)}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 border-r border-gray-100 align-middle text-center">
                    <div className="flex flex-col gap-2 min-w-0 items-center justify-center">
                      {canEditBuenBuroMontoOferta && (
                      <div className="w-full rounded-lg bg-slate-50 border border-slate-200 px-2 py-1.5">
                        <span className="text-xs font-semibold text-slate-600 block mb-1">Oferta</span>
                        <input
                          type="number"
                          min="0"
                          className="w-full px-2 py-1 border border-slate-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-slate-400 focus:border-slate-400"
                          value={buenBuroOferta ?? ''}
                          onKeyDown={(e) => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            if (e.target.value === '' || val >= 0) {
                              s.id && updateFieldLocal(s.id, 'buenBuroMontoOferta', e.target.value === '' ? '' : val);
                            }
                          }}
                          placeholder="0"
                        />
                      </div>
                      )}
                      {canEditBuenBuroEstatus && (
                      <div className="w-full rounded-lg bg-violet-50 border border-violet-200 px-2 py-1.5">
                        <span className="text-xs font-semibold text-violet-700 block mb-1">Estatus</span>
                        <select
                          className="w-full px-2 py-1 border border-violet-300 rounded-md bg-white text-sm focus:ring-2 focus:ring-violet-400"
                          value={buenBuroEstatus}
                          onChange={(e) => s.id && updateFieldLocal(s.id, 'buenBuroEstatus', e.target.value)}
                        >
                          {BUEN_BURO_ESTATUS_OPTIONS.map((o) => (
                            <option key={o.value || '(sin)'} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      )}
                      {canEditBuenBuroMontoAceptado && (
                      <div className="w-full rounded-lg bg-teal-50 border border-teal-200 px-2 py-1.5">
                        <span className="text-xs font-semibold text-teal-700 block mb-1">Aceptado</span>
                        <input
                          type="number"
                          min="0"
                          className="w-full px-2 py-1 border border-teal-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                          value={buenBuroAceptado ?? ''}
                          onKeyDown={(e) => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            if (e.target.value === '' || val >= 0) {
                              s.id && updateFieldLocal(s.id, 'buenBuroMontoAceptado', e.target.value === '' ? '' : val);
                            }
                          }}
                          placeholder="0"
                        />
                      </div>
                      )}
                      {s.id && pendingChanges[s.id] && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => saveRow(s.id!)}
                          disabled={savingRows[s.id!]}
                        >
                          {savingRows[s.id!] ? 'Guardando...' : 'Guardar'}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td className="p-8 text-center text-gray-500" colSpan={10}>
                  Sin solicitudes en Entregado a Riesgos con los filtros actuales.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <SolicitudDetail
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        solicitud={selectedSolicitud}
        onUpdate={handleUpdateFromDetail}
        modulo="mesa-analisis"
      />
    </div>
  );
};

export default MesaDeAnalisisManagement;
